/* eslint-disable no-inline-styles/no-inline-styles */
import { OnboardingEvents, trackTagManagerEvent } from 'firebase-client';
import { FormProvider, SubmitHandler, TextInput, useForm, yupResolver } from 'forms';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addError, Errors } from 'rum';
import { Box, Form, FormHelperText, Typography } from 'ui';
import { Check, Close } from 'ui/icons';
import { object, ref, string } from 'yup';

import { useCreateAccount } from '~/onboarding';
import { ContinueButton, StepTitle } from '~/ui';

interface FormValues {
  password: string;
  confirmPassword: string;
}

const LETTER_REGEX = /[a-z]/;
const CAPITAL_LETTER_REGEX = /[A-Z]/;
const DIGIT_REGEX = /\d/;
const SPECIAL_CHARACTER_REGEX = /[^A-Za-z0-9]/;
const PASSWORD_LENGTH = 8;

interface PasswordRuleProps {
  rule: string;
  isValid: boolean | null;
}

const PasswordRule = ({ rule, isValid }: PasswordRuleProps) => {
  const iconErrorColor = isValid === null ? 'inherit' : 'error';
  let validColor = 'inherit';

  if (isValid !== null) {
    validColor = isValid ? 'primary.dark' : 'red.main';
  }

  return (
    <Box display="flex" flexDirection="row" alignItems="center">
      {isValid ? <Check color="success" fontSize="small" /> : <Close color={iconErrorColor} fontSize="small" />}
      <Typography variant="body2" color={validColor} fontWeight={400}>
        {rule}
      </Typography>
    </Box>
  );
};

export const PasswordStep = () => {
  const { t } = useTranslation('register');
  const { goToNextStep, accountData, createAccount } = useCreateAccount();
  const { email } = accountData;

  const [isLoading, setIsLoading] = useState(false);

  const resolver = yupResolver(
    object({
      confirmPassword: string()
        .required(t('validation:required'))
        .oneOf([ref('password')], t('passwordRequirements.match')),
      password: string()
        .required(t('validation:required'))
        .min(PASSWORD_LENGTH, t('passwordRequirements.length'))
        .matches(LETTER_REGEX, t('passwordRequirements.letter'))
        .matches(CAPITAL_LETTER_REGEX, t('passwordRequirements.capitalLetter'))
        .matches(DIGIT_REGEX, t('passwordRequirements.digit'))
        .matches(SPECIAL_CHARACTER_REGEX, t('passwordRequirements.specialCharacter')),
    })
  );

  const { handleSubmit, setError, formState, watch, ...rest } = useForm<FormValues>({
    defaultValues: { confirmPassword: '', password: '' },
    mode: 'onChange',
    resolver,
  });

  const onSubmit: SubmitHandler<FormValues> = async ({ password }) => {
    trackTagManagerEvent(OnboardingEvents.password);

    await createAccount(
      password,
      (loading) => setIsLoading(loading),
      goToNextStep,
      (name, e) => {
        addError(name, e, { email });

        if (name === Errors.AUTH_SIGN_UP_FAILURE) {
          setError('root', { message: t('register:error'), type: 'submit' });
        }
      }
    );
  };

  const password = watch('password');

  return (
    <FormProvider {...{ formState, handleSubmit, setError, watch, ...rest }}>
      <Form onSubmit={handleSubmit(onSubmit)} style={{ height: '100%' }}>
        <Box height="100%" display="flex" flexDirection="column">
          <StepTitle>{t('register:steps:password:title')}</StepTitle>

          <Box display="flex" flexDirection="column" justifyContent="space-between" flexGrow={1}>
            <Box>
              <TextInput
                required
                margin="normal"
                fullWidth
                type="password"
                label={t('register:steps:password:enterPassword')}
                autocomplete="password"
                name="password"
                autoFocus
                shrink
                data-testid="password"
              />
              <TextInput
                required
                margin="normal"
                fullWidth
                type="password"
                label={t('register:steps:password:confirmPassword')}
                autocomplete="password"
                name="confirmPassword"
                shrink
                data-testid="confirm-password"
              />

              <Box mb={1} mt={1}>
                <Typography variant="body1" color="textSecondary">
                  {t('register:passwordRequirements:title')}
                </Typography>
              </Box>

              <PasswordRule
                rule={t('register:passwordRequirements.length')}
                isValid={formState.isDirty ? password.length >= PASSWORD_LENGTH : null}
              />

              <PasswordRule
                rule={t('register:passwordRequirements.letter')}
                isValid={formState.isDirty ? Boolean(password.match(LETTER_REGEX)) : null}
              />

              <PasswordRule
                rule={t('register:passwordRequirements.capitalLetter')}
                isValid={formState.isDirty ? Boolean(password.match(CAPITAL_LETTER_REGEX)) : null}
              />

              <PasswordRule
                rule={t('register:passwordRequirements.digit')}
                isValid={formState.isDirty ? Boolean(password.match(DIGIT_REGEX)) : null}
              />

              <PasswordRule
                rule={t('register:passwordRequirements.specialCharacter')}
                isValid={formState.isDirty ? Boolean(password.match(SPECIAL_CHARACTER_REGEX)) : null}
              />
            </Box>

            <Box>
              <ContinueButton
                data-testid="account-password-submit-button"
                loading={isLoading}
                disabled={!formState.isValid}
              />
              {formState.errors?.root && <FormHelperText error>{formState?.errors?.root.message}</FormHelperText>}
            </Box>
          </Box>
        </Box>
      </Form>
    </FormProvider>
  );
};
